// App root: declares the palette as CSS variables on the .ipsf root, tracks
// viewport + system dark, restores the session on boot, and shows the login
// flow or the shell.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ApiError } from '@/api/http';
import type { SessionInfo } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { PodMark } from '@/components/ui/Icon';
import { EmptyState } from '@/components/ui/misc';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { STAGING_BANNER_HEIGHT, StagingBanner } from '@/components/ui/StagingBanner';
import { UpdateBanner } from '@/components/ui/UpdateBanner';
import { useRouteSync } from '@/hooks/useRouteSync';
import { useT } from '@/i18n/useT';
import { landingRoute, parseRoute, routePath } from '@/lib/route';
import { KitchenSink } from '@/screens/KitchenSink';
import { LoginScreen } from '@/screens/login/LoginScreen';
import { Shell } from '@/screens/Shell';
import { useStore } from '@/store';
import { readRememberedStation } from '@/store/slices/session';
import { isTablet } from '@/store/slices/ui';
import { buildPalette, paletteVars } from '@/theme/palette';

export function App() {
  const userTheme = useStore((s) => s.userTheme);
  const userAccent = useStore((s) => s.userAccent);
  const sysDark = useStore((s) => s.sysDark);
  const vw = useStore((s) => s.vw);
  const setViewport = useStore((s) => s.setViewport);
  const setSysDark = useStore((s) => s.setSysDark);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [setViewport]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setSysDark(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setSysDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [setSysDark]);

  // iOS Safari ignores user-scalable=no and touch-action for page-level pinch
  // outside standalone mode; its own gesture events are the only lever left.
  useEffect(() => {
    const block = (e: Event) => e.preventDefault();
    const types = ['gesturestart', 'gesturechange', 'gestureend'];
    for (const type of types) document.addEventListener(type, block, { passive: false });
    return () => {
      for (const type of types) document.removeEventListener(type, block);
    };
  }, []);

  const palette = useMemo(() => buildPalette(userTheme, userAccent, sysDark), [userTheme, userAccent, sysDark]);

  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palette.bg);
  }, [palette.bg]);

  const sink = new URLSearchParams(window.location.search).has('sink');

  // The staging strip takes a slice off the top of every full-height screen.
  // Published as a variable so Shell and the sign-in screen can subtract it
  // without knowing whether it is there: on production it is 0px and nothing
  // about those screens changes.
  const staging = useStore((s) => s.deployment?.odooEnvironment === 'staging');
  const bannerVar = { '--sf-banner': staging ? `${STAGING_BANNER_HEIGHT}px` : '0px' } as React.CSSProperties;

  return (
    <div
      className="ipsf"
      style={{
        ...paletteVars(palette),
        ...bannerVar,
        minHeight: '100dvh',
        fontFamily: 'Manrope, sans-serif',
        background: 'var(--stage)',
        color: 'var(--ink)',
      }}
    >
      {/* First, and in the flow: on a staging deployment this is the one thing
          every screen has in common. */}
      <StagingBanner />
      {/* Above everything, signed in or not: a kiosk sitting at the sign-in
          screen overnight is exactly the device an update has to reach. */}
      <UpdateBanner />
      <ErrorBoundary>{sink ? <KitchenSink board={isTablet(vw)} /> : <Boot />}</ErrorBoundary>
    </div>
  );
}

/** Session restore, then either the login flow or the operator app. */
function Boot() {
  const t = useT();
  const qc = useQueryClient();
  const boot = useStore((s) => s.boot);
  const setBoot = useStore((s) => s.setBoot);
  const setConnMode = useStore((s) => s.setConnMode);
  const setOperator = useStore((s) => s.setOperator);
  const setUser = useStore((s) => s.setUser);
  const setIswAvailable = useStore((s) => s.setIswAvailable);
  const setAndonCapabilities = useStore((s) => s.setAndonCapabilities);
  const setAndonFeatures = useStore((s) => s.setAndonFeatures);
  const setServerSkew = useStore((s) => s.setServerSkew);
  const setStation = useStore((s) => s.setStation);
  const setAuthStage = useStore((s) => s.setAuthStage);
  const setScreen = useStore((s) => s.setScreen);
  const goToRoute = useStore((s) => s.goToRoute);
  const setDeployment = useStore((s) => s.setDeployment);
  const operator = useStore((s) => s.operator);
  const [fatal, setFatal] = useState<string | null>(null);

  /** Pick a station: remembered → assigned → busiest → first. Never blind. */
  const resolveStation = useCallback(async () => {
    const [wcs, flowlines] = await Promise.all([api.workcentres(), api.flowlines()]);
    if (wcs.length === 0) return;
    const remembered = readRememberedStation();
    const counts = await api.waitingCounts(remembered?.flowlineId ?? null).catch(() => ({}) as Record<number, number>);

    let chosen = remembered ? wcs.find((w) => w.id === remembered.workcentreId) : undefined;
    if (!chosen) {
      const busiest = [...wcs]
        .filter((w) => (counts[w.id] ?? 0) > 0)
        .sort((a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0))[0];
      chosen = busiest ?? wcs[0];
    }
    const fl = remembered?.flowlineId ? flowlines.find((f) => f.id === remembered.flowlineId) : undefined;
    if (chosen) setStation({ id: chosen.id, name: chosen.name }, fl ? { id: fl.id, name: fl.name } : null);
  }, [setStation]);

  const applySession = useCallback(
    async (info: SessionInfo) => {
      setServerSkew(info.serverNow);
      setDeployment(info.deployment);
      setConnMode(info.authenticated ? (info.mode ?? 'none') : 'none');
      // The leader home is gated on the person's own Odoo account, so it only
      // ever comes from a credentials session — a kiosk's service account says
      // nothing about the operator behind the PIN.
      setUser(
        info.mode === 'user' ? (info.user?.uid ?? null) : null,
        info.mode === 'user' && (info.capabilities?.leader ?? false),
        info.andonAppUrl ?? null,
        info.qaExcludedTeamIds ?? [],
        info.qaDefaultTeamId ?? null,
      );
      setIswAvailable(info.iswAvailable ?? false);
      // Absent on a BFF without the andon module, and absent means off: the
      // app on such a server is the app as it was before the merge began.
      setAndonCapabilities(info.capabilities?.andon);
      setAndonFeatures(info.features?.gifs === true, info.rules ?? null);
      if (info.operator) setOperator(info.operator);
      // An authenticated operator is on a screen, and which one is in the URL.
      // Saying so matters beyond tidiness: `screen` stayed at 'login' after
      // signing in, so everything gated on being at home — the barcode listener
      // among them — stayed switched off until the operator happened to open a
      // job and come back, which is what set it.
      //
      // The URL is still whatever was asked for: nothing has touched history
      // yet (see useRouteSync, which only starts once there is an operator), so
      // a refresh mid-job comes back to that job. A plain "/" is not a request
      // for anything — it is just where the app is served — so it defers to the
      // defaults, which is what puts a leader on the floor rather than at a
      // station.
      //
      // What the URL asked for is read against who is asking: a leader
      // tapping a push lands over the floor, not in their last station. See
      // `landingRoute`.
      if (info.operator) {
        const leader = info.mode === 'user' && (info.capabilities?.leader ?? false);
        const asked = landingRoute(parseRoute(window.location.pathname), leader);
        if (routePath(asked) === '/') setScreen('home');
        else goToRoute(asked);
      }
      if (info.authenticated) await resolveStation().catch(() => {});
      setBoot('ready');
    },
    [
      resolveStation,
      setBoot,
      setConnMode,
      setOperator,
      setUser,
      setServerSkew,
      setDeployment,
      setScreen,
      goToRoute,
      setIswAvailable,
      setAndonCapabilities,
      setAndonFeatures,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await api.sessionInfo();
        if (cancelled) return;
        if (!info.authenticated) {
          setConnMode('none');
          setUser(null, false, info.andonAppUrl ?? null);
          setIswAvailable(info.iswAvailable ?? false);
          setAndonCapabilities(info.capabilities?.andon);
          setServerSkew(info.serverNow);
          setDeployment(info.deployment);
          setAuthStage('chooser');
          setBoot('ready');
          return;
        }
        await applySession(info);
      } catch (e) {
        if (cancelled) return;
        // A factory tool must fail loudly, never quietly show stale/sample data.
        setFatal(e instanceof ApiError ? e.message : t.cantReachServer);
        setBoot('fatal');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Screens get URLs, and the device's Back key gets somewhere to go, from the
  // moment there is an operator to have a screen at all.
  useRouteSync(boot === 'ready' && !!operator);

  if (boot === 'booting') {
    return (
      <div style={{ minHeight: 'calc(100dvh - var(--sf-banner, 0px))', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <PodMark size={44} style={{ color: 'var(--accent)', animation: 'ipPulse 1.6s infinite' }} />
      </div>
    );
  }

  if (boot === 'fatal') {
    return (
      <div style={{ minHeight: 'calc(100dvh - var(--sf-banner, 0px))', display: 'grid', placeItems: 'center', background: 'var(--bg)', padding: 24 }}>
        <EmptyState>
          <PodMark size={38} style={{ color: 'var(--sub)', marginBottom: 14 }} />
          <div style={{ font: "700 16px 'Space Grotesk',sans-serif", color: 'var(--ink)', marginBottom: 6 }}>
            {t.cantReachServer}
          </div>
          <div style={{ marginBottom: 16 }}>{fatal}</div>
          <Button size="md" variant="surface" full={false} onClick={() => window.location.reload()}>
            {t.retry}
          </Button>
        </EmptyState>
      </div>
    );
  }

  if (!operator) {
    return (
      <LoginScreen
        onAuthenticated={async (info) => {
          await applySession(info);
          qc.invalidateQueries();
        }}
      />
    );
  }

  // One shell for everyone. Which layout it takes is the screen's width, and
  // which home it opens on is the session's — see Shell.
  return <Shell />;
}
